import axios from 'axios';
import { createLogger } from '../utils/logger';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  (process.env.NODE_ENV === 'production' 
    ? 'https://ai-travel-920175119171.europe-west3.run.app'
    : 'http://localhost:3001');

const log = createLogger('googleMapsApi');

const GOOGLE_MAPS_LOAD_TIMEOUT_MS = 10000;
export const GOOGLE_MAPS_AUTH_ERROR_MESSAGE = 'Google Maps API key is not authorized for Maps JavaScript API';

declare global {
  interface Window {
    gm_authFailure?: () => void;
  }
}

// Global promise to prevent concurrent loading
let loadingPromise: Promise<void> | null = null;

export const __resetGoogleMapsLoaderForTests = () => {
  if (process.env.NODE_ENV !== 'production') {
    loadingPromise = null;
  }
};

const isGoogleMapsReady = (): boolean => {
  return Boolean(window.google && window.google.maps);
};

const waitForGoogleMapsReady = (): Promise<void> => {
  return new Promise((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      window.clearInterval(intervalId);
      reject(new Error('Timed out loading Google Maps'));
    }, GOOGLE_MAPS_LOAD_TIMEOUT_MS);

    const intervalId = window.setInterval(() => {
      if (isGoogleMapsReady()) {
        window.clearTimeout(timeoutId);
        window.clearInterval(intervalId);
        resolve();
      }
    }, 100);

    if (isGoogleMapsReady()) {
      window.clearTimeout(timeoutId);
      window.clearInterval(intervalId);
      resolve();
    }
  });
};

const installGoogleMapsAuthFailureHandler = (onAuthFailure: () => void): (() => void) => {
  const previousAuthFailure = window.gm_authFailure;

  const handler = () => {
    log.error(GOOGLE_MAPS_AUTH_ERROR_MESSAGE);
    onAuthFailure();

    if (previousAuthFailure) {
      previousAuthFailure();
    }
  };

  window.gm_authFailure = handler;

  return () => {
    if (window.gm_authFailure === handler) {
      window.gm_authFailure = previousAuthFailure;
    }
  };
};

export const getGoogleMapsScriptUrl = async (): Promise<string> => {
  try {
    const response = await axios.get(`${API_BASE_URL}/api/google-maps-script`);
    return response.data.scriptUrl;
  } catch (error: any) {
    log.error('Error getting Google Maps script URL', error);
    throw new Error(error.response?.data?.error || 'Failed to get Google Maps script URL');
  }
};

export const getGoogleMapsEmbedUrl = async (query: string): Promise<string> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/google-maps-embed`, {
      query
    });
    return response.data.embedUrl;
  } catch (error: any) {
    log.error('Error getting Google Maps embed URL', error);
    throw new Error(error.response?.data?.error || 'Failed to get Google Maps embed URL');
  }
};

export const getGoogleMapsSearchUrl = async (query: string): Promise<string> => {
  try {
    const response = await axios.post(`${API_BASE_URL}/api/google-maps-search`, {
      query
    });
    return response.data.searchUrl;
  } catch (error: any) {
    log.error('Error getting Google Maps search URL', error);
    throw new Error(error.response?.data?.error || 'Failed to get Google Maps search URL');
  }
};

export const loadGoogleMapsScript = async (): Promise<void> => {
  // If already loaded, return immediately
  if (isGoogleMapsReady()) {
    log.debug('Google Maps already loaded');
    return Promise.resolve();
  }

  // If currently loading, return the existing promise
  if (loadingPromise) {
    log.debug('Google Maps loading in progress, waiting...');
    return loadingPromise;
  }

  // Check if script tag already exists (might have been added externally)
  if (document.querySelector('script[src*="maps.googleapis.com"]')) {
    log.debug('Google Maps script tag exists, waiting for it to load...');
    loadingPromise = new Promise((resolve, reject) => {
      let cleanupAuthFailure = () => {};
      let settled = false;

      const finish = (callback: () => void) => {
        if (settled) {
          return;
        }
        settled = true;
        cleanupAuthFailure();
        loadingPromise = null;
        callback();
      };

      cleanupAuthFailure = installGoogleMapsAuthFailureHandler(() => {
        finish(() => reject(new Error(GOOGLE_MAPS_AUTH_ERROR_MESSAGE)));
      });

      waitForGoogleMapsReady()
        .then(() => {
          log.debug('Google Maps loaded from existing script');
          finish(() => resolve());
        })
        .catch((error) => {
          log.error('Timed out waiting for existing Google Maps script:', error);
          finish(() => reject(error));
        });
    });
    return loadingPromise;
  }

  // Create new loading promise
  log.debug('Starting Google Maps script load...');
  loadingPromise = new Promise(async (resolve, reject) => {
    let cleanupAuthFailure = () => {};
    let settled = false;
    let script: HTMLScriptElement | null = null;

    const finish = (callback: () => void, removeScript = false) => {
      if (settled) {
        return;
      }
      settled = true;
      cleanupAuthFailure();
      loadingPromise = null;

      if (removeScript && script?.parentNode) {
        script.parentNode.removeChild(script);
      }

      callback();
    };

    try {
      const scriptUrl = await getGoogleMapsScriptUrl();

      script = document.createElement('script');
      script.src = scriptUrl;
      script.async = true;
      script.defer = true;

      cleanupAuthFailure = installGoogleMapsAuthFailureHandler(() => {
        finish(() => reject(new Error(GOOGLE_MAPS_AUTH_ERROR_MESSAGE)), true);
      });

      script.onload = () => {
        waitForGoogleMapsReady()
          .then(() => {
            log.debug('Google Maps script loaded successfully');
            finish(() => resolve());
          })
          .catch((error) => {
            log.error('Google Maps script loaded but API was unavailable:', error);
            finish(() => reject(error), true);
          });
      };

      script.onerror = () => {
        log.error('Failed to load Google Maps script');
        finish(() => reject(new Error('Failed to load Google Maps script')), true);
      };

      document.head.appendChild(script);
    } catch (error) {
      log.error('Error loading Google Maps script:', error);
      finish(() => reject(error as Error), true);
    }
  });

  return loadingPromise;
}; 
